// Pure helpers for the plain-textarea @-mention format.
//
// Stored text carries markdown tokens:  "Hi @[Ada Lovelace](user:7d5c…)"
// The textarea shows readable text:     "Hi @Ada Lovelace"
// A component keeps the picked tokens ({ type, id, label }) alongside the
// visible text and serializes the two back into stored text.
//
// Mirrors Mentions::Extractor::TOKEN_PATTERN on the server.

const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const TOKEN_SOURCE = `@\\[([^\\[\\]\\n]{1,120})\\]\\(([a-z_]{1,20}):(${UUID})\\)`;
const MAX_LABEL = 120;

function tokenRegex() {
  return new RegExp(TOKEN_SOURCE, "gi");
}

function escapeRegExp(text) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Labels end up inside "@[…](…)", so the delimiters and newlines must go.
export function sanitizeLabel(label) {
  return String(label ?? "")
    .replace(/[[\]()]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_LABEL);
}

export function initials(name) {
  const parts = String(name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
}

// Tokens in stored text, in order: [{ type, id, label }].
export function parseTokens(stored) {
  const tokens = [];
  for (const match of String(stored ?? "").matchAll(tokenRegex())) {
    tokens.push({ label: match[1], type: match[2].toLowerCase(), id: match[3].toLowerCase() });
  }
  return tokens;
}

// Stored text → what the textarea shows.
export function toDisplay(stored) {
  return String(stored ?? "").replace(tokenRegex(), (_m, label) => `@${label}`);
}

// Where each picked token sits in the visible text. Each token claims the
// first not-yet-claimed "@Label" followed by a word boundary, so two mentions
// of the same name map in order. Returns [{ start, end, replacement }].
export function claimTokens(display, tokens) {
  const text = String(display ?? "");
  const claimed = [];
  const overlaps = (start, end) => claimed.some((r) => start < r.end && end > r.start);

  for (const token of tokens || []) {
    const label = sanitizeLabel(token.label);
    if (!label || !token.id) continue;
    const re = new RegExp(`@${escapeRegExp(label)}(?=$|[\\s.,;:!?)\\]'"])`, "g");
    let match;
    while ((match = re.exec(text)) !== null) {
      const start = match.index;
      const end = start + match[0].length;
      if (!overlaps(start, end)) {
        claimed.push({ start, end, replacement: `@[${label}](${token.type || "user"}:${token.id})` });
        break;
      }
    }
  }
  return claimed;
}

// Visible text + picked tokens → stored text. A token whose text was deleted
// is dropped (no notification for it).
export function serialize(display, tokens) {
  const claimed = claimTokens(display, tokens).sort((a, b) => b.start - a.start);
  let out = String(display ?? "");
  for (const r of claimed) out = out.slice(0, r.start) + r.replacement + out.slice(r.end);
  return out;
}

// The "@query" being typed just before the caret, or null.
// The @ must start the text or follow whitespace/"(" (so "a@b.no" never opens
// the picker); one inner space is allowed so "@kari be" narrows the search;
// letters are Unicode-aware (Ø, Å, é).
const QUERY_RE = /(^|[\s(])@([\p{L}\p{N}._'-]{0,40}(?: [\p{L}\p{N}._'-]{0,40})?)$/u;

export function detectQuery(text, caret, tokens = []) {
  const before = String(text ?? "").slice(0, caret ?? String(text ?? "").length);
  const match = before.match(QUERY_RE);
  if (!match) return null;

  const start = match.index + match[1].length;
  // Typing right after an already-picked "@Ada Lovelace" is not a new query.
  if (claimTokens(text, tokens).some((r) => r.start === start)) return null;
  return { start, query: match[2] };
}

// Avatar URLs come from the server, but only same-origin paths or https are
// ever put into an <img src>.
export function safeAvatarUrl(url) {
  if (typeof url !== "string") return null;
  if (url.startsWith("/") && !url.startsWith("//")) return url;
  if (url.startsWith("https://")) return url;
  return null;
}

// ---- Plain <-> rich carry-over (log_activity_body.js) -----------------------
// Plain bodies store tokens; Tiptap stores
// <span data-type="mention" data-id="…" data-label="…">@Label</span>.
// Only user tokens become mention nodes (the Tiptap mention node is a
// colleague); any other token type carries over as plain "@Label" text.

const HTML_ESCAPES = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
const UUID_ONLY = new RegExp(`^${UUID}$`, "i");

export function escapeHtml(text) {
  return String(text ?? "").replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);
}

function lineToHtml(line) {
  let out = "";
  let cursor = 0;
  for (const match of line.matchAll(tokenRegex())) {
    out += escapeHtml(line.slice(cursor, match.index));
    const label = escapeHtml(sanitizeLabel(match[1]));
    const type = match[2].toLowerCase();
    out +=
      type === "user"
        ? `<span data-type="mention" class="mention" data-id="${match[3].toLowerCase()}" data-label="${label}">@${label}</span>`
        : `@${label}`;
    cursor = match.index + match[0].length;
  }
  return out + escapeHtml(line.slice(cursor));
}

// Stored plain text → Tiptap HTML: one paragraph per line, everything
// escaped, user tokens as mention spans.
export function storedToHtml(stored) {
  const text = String(stored ?? "");
  if (!text.trim()) return "";
  return text
    .split(/\r?\n/)
    .map((line) => `<p>${lineToHtml(line)}</p>`)
    .join("");
}

// A Tiptap/ProseMirror JSON document (editor.getJSON()) → stored plain text.
// Walks structured data instead of
// parsing HTML: text nodes are copied verbatim, user mention nodes become
// tokens, hard breaks and block nodes become line breaks.
export function docToStored(doc) {
  let out = "";
  const walk = (node) => {
    if (!node || typeof node !== "object") return;
    if (node.type === "text") {
      out += String(node.text || "");
      return;
    }
    if (node.type === "hardBreak") {
      out += "\n";
      return;
    }
    if (node.type === "mention") {
      const id = String(node.attrs?.id || "").trim();
      const label = sanitizeLabel(String(node.attrs?.label || ""));
      if (UUID_ONLY.test(id) && label) {
        out += `@[${label}](user:${id.toLowerCase()})`;
      } else if (label) {
        out += `@${label}`;
      }
      return;
    }
    const isBlock = node.type !== "doc";
    if (isBlock && out && !out.endsWith("\n")) out += "\n";
    for (const child of node.content || []) walk(child);
    if (isBlock && out && !out.endsWith("\n")) out += "\n";
  };
  walk(doc);
  return out.replace(/\n{3,}/g, "\n\n").trim();
}
